import { ReactNode, useId, useState } from 'react';
import { format, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label as LabelUI } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { cn } from '@/lib/utils';

import { DISPLAY_DATES_FORMAT, SEND_DATES_FORMAT } from '@/constants/dates';

function parseDatePickerValue(raw: string, pattern: string): Date | undefined {
  let parsed: Date;
  try {
    parsed = parse(raw, pattern, new Date());
  } catch {
    return undefined;
  }
  if (!isValid(parsed)) {
    return undefined;
  }
  return parsed;
}

type DatePickerProps = {
  value?: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
  dateFormat?: string;
  displayDateFormat?: string;
  'aria-invalid'?: boolean;
  showYearDropdown?: boolean;
  fromYear?: number;
  toYear?: number;
};

const DatePicker = (props: DatePickerProps) => {
  const {
    value,
    onChange,
    label,
    disabled,
    className,
    id,
    dateFormat = SEND_DATES_FORMAT,
    displayDateFormat = DISPLAY_DATES_FORMAT,
    'aria-invalid': ariaInvalid,
    showYearDropdown = true,
    fromYear,
    toYear,
  } = props;

  const generatedId = useId();
  const pickerId = id ?? generatedId;

  const [isOpen, setIsOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState<Date>(() => new Date());

  const dateValue = value ? parseDatePickerValue(value, dateFormat) : undefined;

  const displayValue = dateValue ? format(dateValue, displayDateFormat) : '';

  const isFloating = isOpen || Boolean(value);

  const onDateSelect = (date: Date | undefined) => {
    if (date) {
      const serverValue = format(date, dateFormat);
      onChange?.(serverValue);
    }
    setIsOpen(false);
  };

  const onMonthChange = (month: Date) => {
    setDisplayMonth(month);

    if (dateValue) {
      const currentDay = dateValue.getDate();
      const lastDay = new Date(
        month.getFullYear(),
        month.getMonth() + 1,
        0,
      ).getDate();
      const clampedDay = Math.min(currentDay, lastDay);
      const newDate = new Date(
        month.getFullYear(),
        month.getMonth(),
        clampedDay,
      );
      onChange?.(format(newDate, dateFormat));
    }
  };

  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setDisplayMonth(dateValue || new Date());
    }
  };

  return (
    <div className={cn('relative', className)}>
      <Popover
        open={isOpen}
        onOpenChange={onOpenChange}
      >
        <PopoverTrigger asChild>
          <Button
            id={pickerId}
            variant="outline"
            className={cn(
              'h-10! w-full justify-start border-2 border-transparent! bg-transparent text-left font-normal shadow-none ring-0 outline-none hover:bg-transparent',
              'focus-visible:border-transparent! focus-visible:ring-0 aria-invalid:border-transparent! aria-invalid:ring-0',
              'dark:bg-card dark:hover:bg-card dark:border-transparent!',
              !value && 'text-muted-foreground',
            )}
            type="button"
            disabled={disabled}
            aria-invalid={ariaInvalid}
          >
            <CalendarIcon className="mr-2 size-4" />
            {displayValue || <span>&nbsp;</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
        >
          <Calendar
            mode="single"
            month={displayMonth}
            onMonthChange={onMonthChange}
            selected={dateValue}
            onSelect={onDateSelect}
            autoFocus
            captionLayout={showYearDropdown ? 'dropdown' : 'label'}
            startMonth={new Date(fromYear ?? 1920, 0)}
            endMonth={new Date(toYear ?? new Date().getFullYear() + 10, 11)}
          />
        </PopoverContent>
      </Popover>
      {label && (
        <>
          <fieldset
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-0 top-[-5px] z-1 overflow-hidden rounded-md border-2 border-solid bg-transparent px-[9px] transition-colors',
              isOpen && !ariaInvalid && 'border-primary',
              !isOpen && !ariaInvalid && 'border-input',
              ariaInvalid && 'border-destructive',
            )}
          >
            <legend
              className={cn(
                'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
                isFloating
                  ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
                  : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
              )}
            >
              <span className="visible inline-block px-1 opacity-0">
                {label}
              </span>
            </legend>
          </fieldset>
          <LabelUI
            htmlFor={pickerId}
            className={cn(
              'bg-card text-muted-foreground pointer-events-none absolute z-2 gap-1 rounded-xs px-1 transition-all duration-200 ease-out',
              isFloating
                ? 'top-0 left-[13px] -translate-y-1/2 text-xs'
                : 'top-1/2 left-[37px] -translate-y-1/2 text-sm',
              ariaInvalid ? 'text-destructive' : isOpen && 'text-primary',
            )}
          >
            {label}
          </LabelUI>
        </>
      )}
    </div>
  );
};

export { DatePicker };
export type { DatePickerProps };
